<template>
  <div id="user-list" class="">
    <div class="row justify-content-center">
      <h5 class="ml-auto mr-auto">User List</h5>
      <button
        type="button"
        class="btn btn-light btn-sm"
        data-toggle="modal"
        data-target="#adminAddUserModal"
      >
        <i class="fas fa-add"></i>
      </button>
    </div>
    <hr />

    <CoreDatatable
      v-if="tableData.tableHeaders != null"
      :dataBase="tableData.dataBase"
      :tableHeaders="tableData.tableHeaders"
      :title="tableData.title"
      :dynamicTableId="dataTableID"
      @onTableReady="enableDatatableFeatures"
    />

    <div v-if="loading" class="overlay p-5">
      <i class="fas fa-2x fa-sync-alt fa-spin"></i>
    </div>

    <div class="row justify-content-center">
      <button
        type="button"
        data-toggle="modal"
        data-target="#credentialsSendModal"
        class="btn btn-spnm btn-sm"
        :disabled="selectedUsers.length == 0"
      >
        <i class="fas fa-paper-plane"></i> Send Credentials
      </button>
      <button
        type="button"
        @click="deleteUser"
        class="btn btn-danger btn-sm ml-2"
        :disabled="selectedUsers.length == 0"
      >
        <i class="fas fa-trash-can"></i> Delete User/s
      </button>
    </div>

    <!-- User Info Modal -->
    <UserInfoModal :userData="selectedUser" @editUser="openEditUserModal" />
    <MembershipRegistration @refreshList="getDataBase" />
    <AdminAddUserModal @saved="getDataBase" />
    <AdminEditUserModal :userData="selectedUserForEdit" @saved="getDataBase" />

    <!-- Send Credentials Modal -->
    <div class="modal fade" id="credentialsSendModal" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header bg-spnm text-white">
            <h6 class="modal-title"><i class="fas fa-paper-plane mr-2"></i>Send Credentials</h6>
            <button type="button" class="close text-white" @click="closeCredentialsModal" :disabled="credentialsStep === 2">
              <span>&times;</span>
            </button>
          </div>

          <!-- Step 1: Confirm Subject -->
          <div v-if="credentialsStep === 1">
            <div class="modal-body">
              <p class="text-muted" style="font-size:0.85rem;">
                Sending credentials to <strong>{{ selectedUsers.length }}</strong> user(s).
              </p>
              <div class="form-group">
                <label class="font-weight-bold">Email Subject</label>
                <input type="text" class="form-control form-control-sm" v-model="credentialsSubject" placeholder="Email subject" />
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary btn-sm" @click="closeCredentialsModal">Cancel</button>
              <button type="button" class="btn btn-spnm btn-sm" @click="startSendCredentials" :disabled="!credentialsSubject.trim()">
                <i class="fas fa-paper-plane mr-1"></i> Send to {{ selectedUsers.length }} user(s)
              </button>
            </div>
          </div>

          <!-- Step 2: Progress -->
          <div v-if="credentialsStep === 2">
            <div class="modal-body text-center py-4">
              <i class="fas fa-envelope fa-2x text-primary mb-2"></i>
              <h6>Sending credentials, please wait...</h6>
              <div class="progress mb-2" style="height:22px;border-radius:11px;">
                <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                  :style="{ width: credentialsProgress + '%' }">
                  {{ credentialsProgress }}%
                </div>
              </div>
              <div class="d-flex justify-content-between px-1" style="font-size:0.8rem;">
                <span>{{ credentialsJob.processed }} / {{ credentialsJob.total }} processed</span>
                <span><span class="text-success font-weight-bold">{{ credentialsJob.succeeded }} sent</span> &bull; <span class="text-danger font-weight-bold">{{ credentialsJob.failed }} failed</span></span>
              </div>
            </div>
            <div class="modal-footer"><small class="text-muted"><i class="fas fa-lock mr-1"></i>Please wait...</small></div>
          </div>

          <!-- Step 3: Results -->
          <div v-if="credentialsStep === 3">
            <div class="modal-body">
              <div class="d-flex justify-content-center mb-3">
                <div class="result-badge result-badge-success mr-3">
                  <i class="fas fa-check-circle fa-2x"></i>
                  <div class="font-weight-bold" style="font-size:1.3rem;">{{ credentialsJob.succeeded }}</div>
                  <div style="font-size:0.75rem;">Sent</div>
                </div>
                <div class="result-badge result-badge-danger">
                  <i class="fas fa-times-circle fa-2x"></i>
                  <div class="font-weight-bold" style="font-size:1.3rem;">{{ credentialsJob.failed }}</div>
                  <div style="font-size:0.75rem;">Failed</div>
                </div>
              </div>
              <div v-if="credentialsJob.failed > 0">
                <div class="d-flex justify-content-between mb-1">
                  <span class="font-weight-bold text-danger"><i class="fas fa-exclamation-triangle mr-1"></i>Failed</span>
                  <button class="btn btn-outline-secondary btn-sm" @click="downloadCredentialsFailed"><i class="fas fa-download mr-1"></i>CSV</button>
                </div>
                <div style="max-height:180px;overflow-y:auto;">
                  <table class="table table-sm table-bordered">
                    <thead class="thead-light"><tr><th>#</th><th>Name</th><th>Email</th><th>Error</th></tr></thead>
                    <tbody>
                      <tr v-for="(e, i) in credentialsJob.failedEntries" :key="i">
                        <td>{{ i+1 }}</td><td>{{ e.name }}</td><td>{{ e.email }}</td><td class="text-danger" style="font-size:0.75rem;">{{ e.error }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-success text-center"><i class="fas fa-check-circle fa-lg mr-1"></i>All credentials sent successfully!</div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-spnm btn-sm" @click="closeCredentialsModal"><i class="fas fa-times mr-1"></i>Close</button>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { generate } from "@vue/compiler-core";
import $ from "jquery";
import axios from "axios";
import CoreDatatable from "../Core/Datatable.vue";
import UserInfoModal from "../Admin/UserInfoModal.vue";
import MembershipRegistration from "../Membership/MembershipRegistrationModal.vue";
import AdminAddUserModal from "../Admin/AdminAddUserModal.vue";
import AdminEditUserModal from "../Admin/AdminEditUserModal.vue";
var table;
var databaseLength = 0;

export default {
  name: "UserList",
  components: {
    CoreDatatable,
    UserInfoModal,
    MembershipRegistration,
    AdminAddUserModal,
    AdminEditUserModal,
  },
  data() {
    return {
      dataTableID: "UserListTable",
      selectedUsers: [],
      userInfo: null,
      selectedUser: null,
      selectedUserForEdit: null,
      loading: true,
      tableData: {
        dataBase: [],
        tableHeaders: null,
        title: "User List",
      },
      credentialsStep: 1,
      credentialsSubject: "SPNM Membership Details & Renewal Invitation",
      credentialsJob: { total: 0, processed: 0, succeeded: 0, failed: 0, failedEntries: [] },
      credentialsJobId: null,
      credentialsPollTimer: null,
    };
  },
  created() {},
  computed: {
    credentialsProgress() {
      if (!this.credentialsJob.total) return 0;
      return Math.round((this.credentialsJob.processed / this.credentialsJob.total) * 100);
    },
  },
  mounted() {
    this.getDataBase();
  },
  methods: {
    getDataBase() {
      var requestBody = {};
      var _this = this;
      this.loading = true;
      axios
        .post("/api/User/UserList", requestBody)
        .then(function (response) {
          _this.generateTable(response.data);
          databaseLength = response.data.length;
          _this.loading = false;
        })
        .catch(function (error) {
          _this.loading = false;
          if (error.response) {
            console.log(error.response);
          } else {
            console.log(error);
          }
        });
    },

    generateTable(dataBase) {
      var tableHeaders = [
        {
          title:
            '<input type="checkbox" class=" mt-0 ml-0 select-all" id="selectAll"> ',
          data: "userId",
          className: "dt-center selectAllTHeading",
          orderable: false,
          render: function () {
            return '<input type="checkbox" class=" ml-2 user-select" id="selectCheckbox">';
          },
        },
        {
          title: "Membership No.",
          data: "membershipNo",
          className: "dt-left cursor-pointer",
          render: function (data, type) {
            return (
              '<span class="userInfo"><i id="userInfo" class="fas fa-info-circle mr-1 ml-1"> </i>' +
              data +
              "</span>"
            );
          },
        },
        {
          title: "Membership Status",
          data: "membershipStatus",
          className: "dt-center",
          render: function (data, type) {
            if (data == "Active") {
              return '<span class="badge badge-success text-center">Active</span>';
            } else {
              return '<span class="badge badge-danger text-center">InActive</span>';
            }
          },
        },
        {
          title: "First Name",
          className: "dt-left",
          data: "firstName",
        },
        {
          title: "Last Name",
          className: "dt-left",
          data: "lastName",
        },
        {
          title: "User Type",
          className: "dt-left",
          data: "type",
        },
        {
          title: "Company Name",
          className: "dt-left",
          data: "companyName",
        },
        {
          title: "Job Title",
          className: "dt-left",
          data: "jobTitle",
        },
        {
          title: "Personal Mail ID",
          className: "dt-left",
          data: "personalEmailId",
        },
        {
          title: "Contact Number",
          className: "dt-left",
          data: "personalMobileNo",
        },
      ];

      this.tableData.tableHeaders = tableHeaders;
      this.tableData.dataBase = dataBase;
    },

    enableDatatableFeatures(database) {
      table = $("#dashboardTable" + this.dataTableID).DataTable();

      var _this = this;
      $("#dashboardTable" + this.dataTableID + " tbody").on(
        "click",
        "input.user-select",

        function () {
          var rowdata = table.row($(this).parents("tr")).data();
          let obj = _this.selectedUsers.find(
            (o) => o.userId === rowdata.userId
          );

          if (obj) {
            _this.selectedUsers.splice(
              _this.selectedUsers.findIndex((a) => a.userId === rowdata.userId),
              1
            );
          } else {
            _this.selectedUsers.push(rowdata);
          }

          if (_this.selectedUsers.length == databaseLength) {
            _this.selectedUsers = _this.tableData.dataBase;
            $(".select-all").prop("checked", true);
          } else {
            $(".select-all").prop("checked", false);
          }
        }
      );

      $("#dashboardTable" + this.dataTableID + " tbody").on(
        "click",
        "span.userInfo",

        function () {
          var rowdata = table.row($(this).parents("tr")).data();
          _this.selectedUser = rowdata;
          $("#userInfoModal").modal("toggle");
        }
      );

      $("#dashboardTable" + this.dataTableID + " thead").on(
        "click",
        "input.select-all",

        function () {
          if (_this.selectedUsers.length != parseInt(databaseLength)) {
            _this.selectedUsers = _this.tableData.dataBase;
            $(".user-select").prop("checked", true);
          } else {
            _this.selectedUsers = [];
            $(".user-select").prop("checked", false);
          }
        }
      );
    },

    startSendCredentials() {
      this.credentialsStep = 2;
      this.credentialsJob = { total: this.selectedUsers.length, processed: 0, succeeded: 0, failed: 0, failedEntries: [] };
      var _this = this;
      axios
        .post("/api/Membership/SendCredentials", { UserCollectionItems: this.selectedUsers, Subject: this.credentialsSubject })
        .then(function (response) {
          _this.credentialsJobId = response.data.jobId;
          _this.credentialsPollTimer = setInterval(_this.pollCredentialsProgress, 2000);
        })
        .catch(function (error) {
          _this.credentialsStep = 1;
          alert("Failed to start: " + (error.response ? error.response.data : error.message));
        });
    },

    pollCredentialsProgress() {
      var _this = this;
      axios
        .get("/api/BulkMail/Progress/" + this.credentialsJobId)
        .then(function (response) {
          var d = response.data;
          _this.credentialsJob = { total: d.total, processed: d.processed, succeeded: d.succeeded, failed: d.failed, failedEntries: d.failedEntries || [] };
          if (d.status === "completed" || d.status === "failed") {
            clearInterval(_this.credentialsPollTimer);
            _this.credentialsPollTimer = null;
            _this.credentialsStep = 3;
          }
        })
        .catch(function () {
          clearInterval(_this.credentialsPollTimer);
          _this.credentialsPollTimer = null;
          _this.credentialsStep = 3;
        });
    },

    closeCredentialsModal() {
      this.credentialsStep = 1;
      this.credentialsSubject = "SPNM Membership Details & Renewal Invitation";
      this.credentialsJobId = null;
      this.credentialsJob = { total: 0, processed: 0, succeeded: 0, failed: 0, failedEntries: [] };
      if (this.credentialsPollTimer) { clearInterval(this.credentialsPollTimer); this.credentialsPollTimer = null; }
      $("#credentialsSendModal").modal("hide");
    },

    downloadCredentialsFailed() {
      const rows = [["Name", "Email", "Error"]].concat(
        this.credentialsJob.failedEntries.map(e => [e.name, e.email, e.error])
      );
      const csv = rows.map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(",")).join("\n");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      a.download = "credentials_failed.csv";
      a.click();
    },

    openEditUserModal(userData) {
      this.selectedUserForEdit = userData;
      this.$nextTick(() => {
        $("#userInfoModal").modal("hide");
        $("#adminEditUserModal").modal("show");
      });
    },

    deleteUser() {
      var requestBody = {
        UserPKIds: "",
      };

      this.selectedUsers.forEach((element) => {
        if (requestBody.UserPKIds.length == 0) {
          requestBody.UserPKIds = element.userId.toString();
        } else {
          requestBody.UserPKIds =
            requestBody.UserPKIds + "," + element.userId.toString();
        }
      });

      if (confirm("Are you sure to delete selected users ?")) {
      } else {
        return;
      }

      var _this = this;

      this.loading = true;

      axios
        .post("/api/User/DeleteUsers", requestBody)
        .then(function (response) {
          _this.getDataBase();
          _this.loading = false;
          alert(response.data);
        })
        .catch(function (error) {
          if (error.response) {
            console.log(error.response);
          } else {
            console, log(error);
          }
        });
    },
  },
};
</script>

<style>
.selectAllTHeading {
  padding-bottom: 5.5rem !important;
}
.result-badge {
  display: flex; flex-direction: column; align-items: center;
  padding: 12px 24px; border-radius: 10px;
}
.result-badge-success { background: #d4edda; color: #155724; }
.result-badge-danger  { background: #f8d7da; color: #721c24; }
</style>